import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface OverlayState {
  isMenuOpen: boolean;
  isResortNavOpen: boolean;
  isArtInitiativesOpen: boolean;
  isPackagesOpen: boolean;
  isPackageDetailOpen: boolean;
  selectedPackage: string | null;
  hoveredDestination: string | null;
}

interface OverlayContextType {
  overlayState: OverlayState;
  toggleMenu: () => void;
  toggleResortNav: () => void;
  toggleArtInitiatives: () => void;
  togglePackages: () => void;
  openPackageDetail: (packageId: string) => void;
  closePackageDetail: () => void;
  closeAllOverlays: () => void;
  closeMenu: () => void;
  setHoveredDestination: (destination: string | null) => void;
}

const OverlayContext = createContext<OverlayContextType | undefined>(undefined);

export const OverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [overlayState, setOverlayState] = useState<OverlayState>({
    isMenuOpen: false,
    isResortNavOpen: false,
    isArtInitiativesOpen: false,
    isPackagesOpen: false,
    isPackageDetailOpen: false,
    selectedPackage: null,
    hoveredDestination: null
  });

  // Prevent body scroll when any overlay is open
  useEffect(() => {
    const anyOverlayOpen = overlayState.isMenuOpen || overlayState.isResortNavOpen || overlayState.isArtInitiativesOpen || overlayState.isPackagesOpen || overlayState.isPackageDetailOpen;
    
    if (anyOverlayOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [overlayState]);

  const toggleMenu = useCallback(() => {
    setOverlayState(prev => ({
      isMenuOpen: !prev.isMenuOpen,
      isResortNavOpen: false,
      isArtInitiativesOpen: false, // Always close sub-sections when toggling menu
      isPackagesOpen: false,
      isPackageDetailOpen: false,
      selectedPackage: null,
      hoveredDestination: null
    }));
  }, []);

  const toggleResortNav = useCallback(() => {
    setOverlayState(prev => ({
      ...prev,
      isResortNavOpen: !prev.isResortNavOpen
    }));
  }, []);

  const toggleArtInitiatives = useCallback(() => {
    setOverlayState(prev => ({
      isMenuOpen: prev.isMenuOpen, // Keep menu state
      isResortNavOpen: prev.isResortNavOpen, // Keep resort nav state
      isArtInitiativesOpen: !prev.isArtInitiativesOpen,
      isPackagesOpen: false,
      isPackageDetailOpen: false,
      selectedPackage: null,
      hoveredDestination: null
    }));
  }, []);

  const togglePackages = useCallback(() => {
    setOverlayState(prev => ({
      isMenuOpen: prev.isMenuOpen, // Keep menu state
      isResortNavOpen: prev.isResortNavOpen, // Keep resort nav state
      isArtInitiativesOpen: false,
      isPackagesOpen: !prev.isPackagesOpen,
      isPackageDetailOpen: false,
      selectedPackage: null,
      hoveredDestination: null
    }));
  }, []);

  const openPackageDetail = useCallback((packageId: string) => {
    setOverlayState(prev => ({
      ...prev,
      isPackagesOpen: false,
      isPackageDetailOpen: true,
      selectedPackage: packageId
    }));
  }, []);

  const closePackageDetail = useCallback(() => {
    setOverlayState(prev => ({
      ...prev,
      isPackageDetailOpen: false,
      selectedPackage: null
    }));
  }, []);

  const closeAllOverlays = useCallback(() => {
    setOverlayState({
      isMenuOpen: false,
      isResortNavOpen: false,
      isArtInitiativesOpen: false,
      isPackagesOpen: false,
      isPackageDetailOpen: false,
      selectedPackage: null,
      hoveredDestination: null
    });
  }, []);

  const closeMenu = useCallback(() => {
    setOverlayState({
      isMenuOpen: false,
      isResortNavOpen: false,
      isArtInitiativesOpen: false, // Reset all sub-sections when closing menu
      isPackagesOpen: false,
      isPackageDetailOpen: false,
      selectedPackage: null,
      hoveredDestination: null
    });
  }, []);

  const setHoveredDestination = useCallback((destination: string | null) => {
    setOverlayState(prev => ({
      ...prev,
      hoveredDestination: destination
    }));
  }, []);

  return (
    <OverlayContext.Provider value={{
      overlayState,
      toggleMenu,
      toggleResortNav,
      toggleArtInitiatives,
      togglePackages,
      openPackageDetail,
      closePackageDetail,
      closeAllOverlays,
      closeMenu,
      setHoveredDestination
    }}>
      {children}
    </OverlayContext.Provider>
  );
};

export const useOverlayManager = () => {
  const context = useContext(OverlayContext);
  if (context === undefined) {
    throw new Error('useOverlayManager must be used within an OverlayProvider');
  }
  return context;
};